import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import templateService from '../services/templateService'
import SchemaEditor from './SchemaEditor'
import RegionalSearchConfig from './RegionalSearchConfig'
import GenerateSchemaConfirmModal from './GenerateSchemaConfirmModal'

const TemplateManager = () => {
  const { t } = useTranslation()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [showEditor, setShowEditor] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    entity_type: 'help_offer',
    llm_instruction: '',
    llm_provider: 'openai/gpt-4o-mini',
    llm_cache: false,
    search_query_template: '',
    default_search_query: '',
    max_results: 10,
    tags: [],
    prequalification_config: {
      enabled: false,
      inclusion_criteria: '',
      exclusion_criteria: '',
      llm_provider: 'openai/gpt-4o-mini',
      temperature: 0.1
    },
    regional_config: {
      enabled: false,
      state_code: '',
      cities: [],
      include_zip: true,
      include_city: true
    }
  })
  const [schema, setSchema] = useState({})
  const [showGenerateSchemaModal, setShowGenerateSchemaModal] = useState(false)
  const [generatedSchemaPreview, setGeneratedSchemaPreview] = useState(null)
  const [isGeneratingSchema, setIsGeneratingSchema] = useState(false)

  const fieldsToSchema = (fields) => {
    const s = {}
    ;(fields || []).forEach((f) => {
      s[f.field_name] = {
        type: f.type || 'string',
        description: f.description || '',
        display_title: f.display_title || (f.field_name && f.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())) || '',
        required: !!f.required
      }
    })
    return s
  }

  const handleGenerateSchemaFromInstruction = async (instruction) => {
    const text = (instruction || formData.llm_instruction || '').trim()
    if (!text) {
      toast.error(t('schemaEditor.generateAiNeedInstruction'))
      return
    }
    setIsGeneratingSchema(true)
    try {
      const data = await templateService.generateSchema(text)
      setGeneratedSchemaPreview(data)
      setShowGenerateSchemaModal(true)
    } catch (err) {
      const msg = err.response?.data?.detail || err.message
      toast.error(msg || 'Schema generation failed')
    } finally {
      setIsGeneratingSchema(false)
    }
  }

  const handleConfirmGeneratedSchema = (replaceOrMerge) => {
    if (!generatedSchemaPreview?.fields?.length) return
    const newSchema = fieldsToSchema(generatedSchemaPreview.fields)
    if (replaceOrMerge === 'replace') {
      setSchema(newSchema)
      toast.success(t('schemaEditor.generateModal.replace') + ' – ' + generatedSchemaPreview.fields.length + ' ' + t('schemaEditor.fields'))
    } else {
      setSchema((prev) => ({ ...prev, ...newSchema }))
      toast.success(t('schemaEditor.generateModal.merge') + ' – ' + generatedSchemaPreview.fields.length + ' ' + t('schemaEditor.fields'))
    }
    setShowGenerateSchemaModal(false)
    setGeneratedSchemaPreview(null)
  }

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      const data = await templateService.getTemplates()
      setTemplates(data.templates || [])
    } catch (error) {
      toast.error(t('templates.toast.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingTemplate(null)
    setFormData({
      name: '',
      description: '',
      entity_type: 'help_offer',
      llm_instruction: '',
      llm_provider: 'openai/gpt-4o-mini',
      llm_cache: false,
      search_query_template: '',
      default_search_query: '',
      max_results: 10,
      tags: [],
      prequalification_config: {
        enabled: false,
        inclusion_criteria: '',
        exclusion_criteria: '',
        llm_provider: 'openai/gpt-4o-mini',
        temperature: 0.1
      },
      regional_config: {
        enabled: false,
        state_code: '',
        cities: [],
        include_zip: true,
        include_city: true
      }
    })
    setSchema({})
    setShowEditor(true)
  }

  const handleEdit = (template) => {
    setEditingTemplate(template)
    const regionalConfig = template.regional_config || {
      enabled: false,
      state_code: '',
      cities: [],
      include_zip: true,
      include_city: true
    }
    
    setFormData({
      name: template.name,
      description: template.description || '',
      entity_type: template.entity_type,
      llm_instruction: template.llm_instruction,
      llm_provider: template.llm_provider,
      llm_cache: template.llm_cache,
      search_query_template: template.search_query_template || '',
      default_search_query: template.default_search_query || '',
      max_results: template.max_results,
      tags: template.tags || [],
      prequalification_config: template.prequalification_config || {
        enabled: false,
        inclusion_criteria: '',
        exclusion_criteria: '',
        llm_provider: 'openai/gpt-4o-mini',
        temperature: 0.1
      },
      regional_config: regionalConfig
    })
    setSchema(template.schema?.properties || {})
    setShowEditor(true)
  }

  const handleSave = async () => {
    if (isSaving) return // Prevent double-clicks
    
    try {
      setIsSaving(true)
      
      // Dismiss all existing toasts to prevent confusion
      toast.dismiss()
      
      const templateData = {
        ...formData,
        schema: {
          type: 'object',
          properties: schema,
          required: Object.keys(schema).filter(k => schema[k].required)
        }
      }

      if (editingTemplate) {
        await templateService.updateTemplate(editingTemplate.id, templateData)
        toast.success(t('templates.toast.updated'))
      } else {
        await templateService.createTemplate(templateData)
        toast.success(t('templates.toast.created'))
      }

      setShowEditor(false)
      fetchTemplates()
    } catch (error) {
      console.error('Template save error:', error)
      toast.error(t('templates.toast.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t('templates.toast.deleteConfirm'))) return
    
    try {
      await templateService.deleteTemplate(id)
      toast.success(t('templates.toast.deleted'))
      fetchTemplates()
    } catch (error) {
      toast.error(t('templates.toast.deleteFailed'))
    }
  }

  const handleDuplicate = (template) => {
    setEditingTemplate(null)
    setFormData({
      ...formData,
      name: template.name + ' (Copy)',
      description: template.description,
      entity_type: template.entity_type,
      llm_instruction: template.llm_instruction,
      llm_provider: template.llm_provider,
      tags: template.tags
    })
    setSchema(template.schema?.properties || {})
    setShowEditor(true)
  }

  const exportTemplate = (template) => {
    const json = JSON.stringify(template, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `template_${template.name.replace(/\s+/g, '_')}.json`
    link.click()
    URL.revokeObjectURL(url)
    toast.success(t('templates.toast.exported'))
  }

  const importTemplate = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async (e) => {
      try {
        const file = e.target.files[0]
        if (!file) return

        const text = await file.text()
        const templateData = JSON.parse(text)

        // Create new template from import
        await templateService.createTemplate({
          name: templateData.name || templateData.template?.name,
          description: templateData.description || templateData.template?.description,
          entity_type: templateData.entity_type || templateData.template?.entity_type || 'help_offer',
          schema: templateData.schema || templateData.template?.schema || {},
          llm_instruction: templateData.llm_instruction || templateData.template?.llm_instruction || '',
          llm_provider: templateData.llm_provider || templateData.template?.llm_provider || 'openai/gpt-4o-mini',
          llm_cache: templateData.llm_cache || templateData.template?.llm_cache || false,
          search_query_template: templateData.search_query_template || templateData.template?.search_query_template || '',
          default_search_query: templateData.default_search_query || templateData.template?.default_search_query || '',
          max_results: templateData.max_results || templateData.template?.max_results || 10,
          tags: templateData.tags || templateData.template?.tags || [],
          prequalification_config: templateData.prequalification_config || templateData.template?.prequalification_config || {
            enabled: false,
            inclusion_criteria: '',
            exclusion_criteria: '',
            llm_provider: 'openai/gpt-4o-mini',
            temperature: 0.1
          },
          regional_config: templateData.regional_config || templateData.template?.regional_config || {
            enabled: false,
            state_code: '',
            cities: [],
            include_zip: true,
            include_city: true
          }
        })

        toast.success('✅ Template imported successfully!')
        fetchTemplates()
      } catch (error) {
        console.error('Import failed:', error)
        toast.error('❌ Failed to import template: ' + error.message)
      }
    }
    input.click()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-12 w-12">
          <i className="fa-solid fa-spinner text-primary-500 text-4xl"></i>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="card border-l-4 border-l-primary-500">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-dark-300">{t('templates.title')}</h2>
              <p className="text-dark-100">
                {templates.length === 1 
                  ? t('templates.subtitle', { count: templates.length })
                  : t('templates.subtitlePlural', { count: templates.length })
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={importTemplate}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold shadow-lg transition-all flex items-center gap-2"
              title="Import Template"
            >
              <i className="fa-solid fa-upload"></i>
              {t('templates.importTemplate') || 'Vorlage importieren'}
            </button>
            <button
              onClick={handleCreate}
              className="px-6 py-3 gradient-primary hover:opacity-90 text-white rounded-lg font-semibold shadow-lg transition-all"
            >
              <i className="fa-solid fa-plus mr-2"></i>
              {t('templates.createTemplate')}
            </button>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {templates.length === 0 ? (
          <div className="col-span-2 card text-center py-12">
            <div className="bg-light-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fa-solid fa-layer-group text-light-200 text-4xl"></i>
            </div>
            <h3 className="text-xl font-semibold text-dark-300 mb-2">{t('templates.noTemplatesTitle')}</h3>
            <p className="text-dark-100 mb-4">{t('templates.noTemplatesDesc')}</p>
            <button
              onClick={handleCreate}
              className="px-6 py-3 gradient-primary hover:opacity-90 text-white rounded-lg font-semibold shadow-lg inline-flex items-center"
            >
              <i className="fa-solid fa-plus mr-2"></i>
              {t('templates.createTemplate')}
            </button>
          </div>
        ) : (
          templates.map((template) => (
            <div key={template.id} className="card hover:shadow-xl transition-all border-2 border-light-200/20 hover:border-primary-500/30">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-dark-300 mb-1">{template.name}</h3>
                  <p className="text-sm text-dark-100">{template.description || 'No description'}</p>
                </div>
                <span className="px-3 py-1 bg-primary-100 text-primary-800 text-xs font-bold rounded-full">
                  {template.entity_type}
                </span>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="flex items-center gap-2 p-3 bg-light-100 rounded-lg">
                  <i className="fa-solid fa-table-columns text-primary-500 text-lg"></i>
                  <div>
                    <div className="text-xs text-dark-100">{t('templates.card.fields')}</div>
                    <div className="font-bold text-dark-300">{Object.keys(template.schema?.properties || {}).length}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 bg-light-100 rounded-lg">
                  <i className="fa-solid fa-rotate text-primary-800 text-lg"></i>
                  <div>
                    <div className="text-xs text-dark-100">{t('templates.card.used')}</div>
                    <div className="font-bold text-dark-300">{template.usage_count || 0}{t('templates.card.times')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 bg-light-100 rounded-lg col-span-2">
                  <i className="fa-solid fa-robot text-primary-500 text-lg"></i>
                  <div className="flex-1">
                    <div className="text-xs text-dark-100">{t('templates.card.aiModel')}</div>
                    <div className="font-semibold text-dark-300 text-sm truncate">{template.llm_provider}</div>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {Array.isArray(template.tags) && template.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-light-200/30">
                  {template.tags.map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 bg-primary-50 text-primary-800 text-xs rounded-md font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={() => handleEdit(template)}
                  className="col-span-2 px-4 py-2 gradient-primary hover:opacity-90 text-white rounded-lg text-sm font-semibold transition-all shadow-md"
                >
                  <i className="fa-solid fa-pen mr-1"></i>
                  {t('templates.card.edit')}
                </button>
                <button
                  onClick={() => handleDuplicate(template)}
                  className="px-3 py-2 bg-white border-2 border-light-200/30 hover:border-primary-500 hover:bg-light-100 text-dark-300 rounded-lg text-sm font-semibold transition-all"
                  title="Duplicate"
                >
                  <i className="fa-solid fa-copy"></i>
                </button>
                <button
                  onClick={() => exportTemplate(template)}
                  className="px-3 py-2 bg-white border-2 border-light-200/30 hover:border-primary-500 hover:bg-light-100 text-dark-300 rounded-lg text-sm font-semibold transition-all"
                  title="Export"
                >
                  <i className="fa-solid fa-download"></i>
                </button>
              </div>
              <button
                onClick={() => handleDelete(template.id)}
                className="w-full mt-2 px-3 py-2 bg-white border-2 border-light-200/30 hover:border-red-500 hover:bg-red-50 text-dark-300 hover:text-red-600 rounded-lg text-sm font-semibold transition-all"
              >
                <i className="fa-solid fa-trash mr-1"></i>
                {t('templates.card.deleteTemplate')}
              </button>
            </div>
          ))
        )}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-light-200/20 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-dark-300">
                  {editingTemplate ? t('templates.editor.titleEdit') : t('templates.editor.titleCreate')}
                </h3>
                <button onClick={() => {
                  setShowEditor(false)
                }} className="text-dark-100 hover:text-dark-300 transition-colors">
                  <i className="fa-solid fa-xmark text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-bold text-dark-300 mb-2">{t('templates.editor.name')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input-field"
                  placeholder={t('templates.editor.namePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-dark-300 mb-2">{t('templates.editor.description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="input-field"
                  rows={3}
                  placeholder={t('templates.editor.descriptionPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-dark-300 mb-2">Entity Type *</label>
                <div className="relative">
                  <select
                    value={formData.entity_type}
                    onChange={(e) => setFormData({...formData, entity_type: e.target.value})}
                    className="input-field appearance-none"
                  >
                    <option value="help_offer">Help Offer</option>
                    <option value="organisation">Organisation</option>
                    <option value="organisation_unit">Organisation Unit</option>
                    <option value="address">Address</option>
                    <option value="contact_person">Contact Person</option>
                    <option value="data">Data</option>
                    <option value="custom">Custom</option>
                  </select>
                  <i className="fa-solid fa-chevron-down absolute right-4 top-4 text-light-200 pointer-events-none"></i>
                </div>
              </div>

              <SchemaEditor
                schema={schema}
                onChange={setSchema}
                llmInstruction={formData.llm_instruction}
                onGenerateSchema={handleGenerateSchemaFromInstruction}
                isGeneratingSchema={isGeneratingSchema}
              />

              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <label className="block text-sm font-bold text-dark-300">LLM Instruction *</label>
                  <button
                    type="button"
                    disabled={isGeneratingSchema || !formData.llm_instruction?.trim()}
                    onClick={() => handleGenerateSchemaFromInstruction(formData.llm_instruction)}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t('schemaEditor.generateWithAi')}
                  >
                    {isGeneratingSchema ? (
                      <><i className="fa-solid fa-spinner fa-spin"></i> {t('schemaEditor.generateLoading')}</>
                    ) : (
                      <><i className="fa-solid fa-wand-magic-sparkles"></i> {t('schemaEditor.generateWithAi')}</>
                    )}
                  </button>
                </div>
                <textarea
                  value={formData.llm_instruction}
                  onChange={(e) => setFormData({...formData, llm_instruction: e.target.value})}
                  className="input-field font-mono text-sm"
                  rows={8}
                  placeholder="Extract all help offer information..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-dark-300 mb-2">LLM Provider</label>
                  <div className="relative">
                    <select
                      value={formData.llm_provider}
                      onChange={(e) => setFormData({...formData, llm_provider: e.target.value})}
                      className="input-field appearance-none"
                    >
                      <option value="openai/gpt-4o-mini">OpenAI GPT-4o-mini</option>
                      <option value="openai/gpt-4o">OpenAI GPT-4o</option>
                      <option value="anthropic/claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</option>
                      <option value="inference-net/schematron-8b">Inference.net Schematron 8B</option>
                      <option value="inference-net/schematron-3b">Inference.net Schematron 3B</option>
                    </select>
                    <i className="fa-solid fa-chevron-down absolute right-4 top-4 text-light-200 pointer-events-none"></i>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-dark-300 mb-2">Max Results</label>
                  <input
                    type="number"
                    value={formData.max_results}
                    onChange={(e) => setFormData({...formData, max_results: parseInt(e.target.value)})}
                    className="input-field"
                    min="1"
                    max="100000"
                  />
                </div>
              </div>

              {/* Default Search Query */}
              <div>
                <label className="block text-sm font-bold text-dark-300 mb-2">
                  Was suchen Sie? (Default Search Query)
                </label>
                <input
                  type="text"
                  value={formData.default_search_query}
                  onChange={(e) => setFormData({...formData, default_search_query: e.target.value})}
                  className="input-field"
                  placeholder="z.B. 'Tafel Essensausgabe' oder 'Notunterkunft'"
                />
                <p className="text-xs text-dark-100 mt-1">
                  Standard-Suchanfrage, die beim Laden des Templates verwendet wird
                </p>
              </div>

              <GenerateSchemaConfirmModal
                open={showGenerateSchemaModal}
                onClose={() => { setShowGenerateSchemaModal(false); setGeneratedSchemaPreview(null) }}
                generatedFields={generatedSchemaPreview?.fields || []}
                onConfirm={handleConfirmGeneratedSchema}
              />

              {/* Regional Search Configuration */}
              <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50/30">
                <div className="flex items-center gap-2 mb-4">
                  <i className="fa-solid fa-map-marker-alt text-purple-600"></i>
                  <h4 className="text-lg font-bold text-dark-300">Regional Search (Optional)</h4>
                </div>
                
                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.regional_config.enabled}
                      onChange={(e) => setFormData({
                        ...formData,
                        regional_config: {
                          ...formData.regional_config,
                          enabled: e.target.checked
                        }
                      })}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-sm font-semibold text-dark-300">Regional Search aktivieren</span>
                  </label>
                  <p className="text-xs text-dark-100 mt-1 ml-6">
                    Suche wird mit PLZ+Ort kombiniert für lokale Ergebnisse
                  </p>
                </div>

                {formData.regional_config.enabled && (
                  <RegionalSearchConfig
                    regionalConfig={formData.regional_config}
                    onRegionalConfigChange={(newConfig) => {
                      setFormData({
                        ...formData,
                        regional_config: newConfig
                      })
                    }}
                    searchQuery={formData.default_search_query}
                    showSearchPreview={true}
                  />
                )}
              </div>

              {/* Prequalification Configuration */}
              <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/30">
                <div className="flex items-center gap-2 mb-4">
                  <i className="fa-solid fa-filter text-blue-600"></i>
                  <h4 className="text-lg font-bold text-dark-300">KI-Präqualifizierung (Optional)</h4>
                </div>
                
                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.prequalification_config.enabled}
                      onChange={(e) => setFormData({
                        ...formData,
                        prequalification_config: {
                          ...formData.prequalification_config,
                          enabled: e.target.checked
                        }
                      })}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm font-semibold text-dark-300">Aktivieren</span>
                  </label>
                  <p className="text-xs text-dark-100 mt-1 ml-6">
                    Die KI filtert automatisch Suchergebnisse basierend auf Ihren Kriterien.
                  </p>
                </div>

                {formData.prequalification_config.enabled && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-dark-300 mb-2">
                        Einschlusskriterien (Was MUSS vorhanden sein)
                      </label>
                      <textarea
                        value={formData.prequalification_config.inclusion_criteria}
                        onChange={(e) => setFormData({
                          ...formData,
                          prequalification_config: {
                            ...formData.prequalification_config,
                            inclusion_criteria: e.target.value
                          }
                        })}
                        className="input-field text-sm"
                        rows={3}
                        placeholder="z.B. 'Die Seite muss eine echte Essensausgabe vor Ort beschreiben...'"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-dark-300 mb-2">
                        Ausschlusskriterien (Was darf NICHT vorhanden sein)
                      </label>
                      <textarea
                        value={formData.prequalification_config.exclusion_criteria}
                        onChange={(e) => setFormData({
                          ...formData,
                          prequalification_config: {
                            ...formData.prequalification_config,
                            exclusion_criteria: e.target.value
                          }
                        })}
                        className="input-field text-sm"
                        rows={3}
                        placeholder="z.B. 'Keine Zeitungsberichte, keine TV-Berichte...'"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-dark-300 mb-2">LLM Provider</label>
                        <select
                          value={formData.prequalification_config.llm_provider}
                          onChange={(e) => setFormData({
                            ...formData,
                            prequalification_config: {
                              ...formData.prequalification_config,
                              llm_provider: e.target.value
                            }
                          })}
                          className="input-field text-sm"
                        >
                          <option value="openai/gpt-4o-mini">OpenAI GPT-4o-mini</option>
                          <option value="openai/gpt-4o">OpenAI GPT-4o</option>
                          <option value="anthropic/claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</option>
                          <option value="inference-net/schematron-8b">Inference.net Schematron 8B</option>
                          <option value="inference-net/schematron-3b">Inference.net Schematron 3B</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-dark-300 mb-2">Temperature</label>
                        <input
                          type="number"
                          value={formData.prequalification_config.temperature}
                          onChange={(e) => setFormData({
                            ...formData,
                            prequalification_config: {
                              ...formData.prequalification_config,
                              temperature: parseFloat(e.target.value)
                            }
                          })}
                          className="input-field text-sm"
                          step="0.1"
                          min="0"
                          max="2"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-light-200/20">
                <button
                  onClick={() => {
                    setShowEditor(false)
                  }}
                  className="px-6 py-3 bg-light-100 text-dark-300 rounded-lg hover:bg-light-200 font-semibold transition-colors"
                >
                  {t('templates.editor.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-6 py-3 gradient-primary hover:opacity-90 text-white rounded-lg font-semibold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className={`fa-solid ${isSaving ? 'fa-spinner fa-spin' : 'fa-check'} mr-2`}></i>
                  {isSaving ? 'Speichern...' : (editingTemplate ? t('templates.editor.updateBtn') : t('templates.editor.createBtn'))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TemplateManager
